/**
 * Who gets the survey — the whole question, in one place, written as the sentence it is.
 *
 * **Every audience decision lives here**, including the use case. Splitting it across two blocks
 * is what let someone aim a survey at Água while hanging it off a feature only offered to Gás: the
 * two conditions join with AND, the intersection is empty, and the survey ends up approved, active,
 * inside its window and reaching nobody — with no error and a blank results screen (R10b).
 *
 * The rule is not five independent settings; it has a grammar, and the grammar takes two shapes:
 *
 * - no condition: *Recebe a pesquisa **todo usuário da BU***, or *quem for do use case **Água***;
 * - conditioned: *quem estiver **com a funcionalidade ligada** em **mdm-menu** há pelo menos **7**
 *   dias, contados **desde a última vez que ligou***.
 *
 * As a grid of labelled boxes the relationship between the fields is invisible and each box needs
 * two lines of prose. As the sentence, the grammar carries the relationship and the prose
 * disappears: "dias de uso · dias corridos, 24h × N · o padrão é 7" becomes the number `7` with the
 * word "dias" next to it.
 *
 * With a condition on, the use case **leaves the sentence**: the audience is already defined by
 * whoever could turn the feature on, and narrowing further is a refinement, not the rule. It moves
 * into "Refinar quem recebe", offering **only that feature's use cases** — so the contradiction
 * has nowhere to be written rather than being written and then warned about.
 *
 * Under the sentence, the rule is echoed in plain Portuguese. That echo is where someone notices
 * they picked "desligada" without meaning to: three selects can each be read correctly and still
 * add up to something nobody intended.
 *
 * @module Pages/BUs/Surveys
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import { UTTextField } from '@group-link-one/gl-ui/utilities';
import { useState } from 'react';
import type { JSX } from 'react';
import type { DelayAnchor, DelayUnit, OptInState } from '../../../Types/Surveys.types';
import type { SurveyFormData } from '../../../Utils/surveyForm';
import {
  ELIGIBILITY_ANCHOR_CLAUSES,
  ELIGIBILITY_STATE_CLAUSES,
  ELIGIBILITY_UNIT_CLAUSES,
  eligibilitySentence,
} from '../../../Utils/surveyFormat';

const panelStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
`;

const titleStyle = css`
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  letter-spacing: var(--letter-spacing-tight);
`;

const toggleRowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-s);
  cursor: pointer;
`;

const toggleTextStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const toggleLabelStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const toggleHintStyle = css`
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

/**
 * The sentence itself.
 *
 * A `div`, not a `p`: the number in the middle is a `UTTextField`, which renders a `div`, and a
 * `div` inside a `p` is invalid HTML — React logs a hydration error for it. It reads as a
 * paragraph, which is what matters.
 *
 * `line-height` is generous because the inline controls are taller than the text around them; at a
 * normal line height the rows collide the moment the sentence wraps.
 */
const sentenceStyle = css`
  margin: 0;
  padding: var(--space-m) var(--space-ml);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-m);
  background: var(--color-surface-sunken);
  font-size: var(--font-size-md);
  line-height: 2.4;
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

/** A control that reads as a word in the sentence: underlined, sized to its own content. */
const inlineSelectStyle = css`
  height: 32px;
  margin: 0 2px;
  padding: 0 var(--space-xs);
  border: none;
  border-bottom: 1px solid var(--color-primary);
  border-radius: var(--radius-control) var(--radius-control) 0 0;
  background: var(--color-surface);
  color: var(--color-primary);
  font-family: inherit;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  max-width: 100%;

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 1px;
  }
`;

const missingSelectStyle = css`
  border-bottom-color: var(--color-danger);
  color: var(--color-danger);
`;

const inlineNumberStyle = css`
  display: inline-flex;
  width: 68px;
  margin: 0 2px;
  vertical-align: middle;
`;

/** A `div` for the same reason as the sentence: `GLIcon` renders one, and a `div` inside a `p` is
    invalid HTML. */
const echoStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-xs);
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text);
  text-wrap: pretty;
`;

const errorStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-danger);
`;

const refineToggleStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-secondary);
  font-family: inherit;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;

  &:hover {
    color: var(--color-primary);
  }
`;

const refineBodyStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-ml);
  padding: var(--space-m) var(--space-ml);
  border: 1px dashed var(--color-border-strong);
  border-radius: var(--radius-m);
`;

const refineFieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const labelStyle = css`
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
`;

const refineHintStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  max-width: 44ch;
  text-wrap: pretty;
`;

const refineSelectStyle = css`
  height: 40px;
  min-width: 240px;
  padding: 0 var(--space-s);
  border-radius: var(--radius-control);
  border: 1px solid var(--color-border-strong);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: inherit;
  font-size: var(--font-size-sm);
`;

const conflictStyle = css`
  max-width: 44ch;
  font-size: var(--font-size-2xs);
  color: var(--color-danger);
  text-wrap: pretty;
`;

const narrowFieldStyle = css`
  display: block;
  width: 120px;
`;

const dateInputStyle = css`
  height: 40px;
  padding: 0 var(--space-s);
  border-radius: var(--radius-control);
  border: 1px solid var(--color-border-strong);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: inherit;
  font-size: var(--font-size-sm);
`;

/** Reads as a stated value, not as a disabled control nobody can tell from a broken one. */
const lockedUseCaseStyle = css`
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
`;

export interface SurveyEligibilityBlockProps {
  form: SurveyFormData;
  /** The tenant's opt-in features, as options. Empty while no BU is chosen. */
  featureOptions: { id: string; name: string; key: string }[];
  /**
   * The use cases the survey may be narrowed to.
   *
   * Already restricted by the caller to the chosen feature's own use cases when there is a
   * condition, which is what keeps the empty intersection unwritable (R10b).
   */
  useCaseOptions: { id: string; name: string }[];
  /** Set only when data already in the BFF holds a combination this screen can no longer build. */
  conflict?: { featureName: string; useCaseName: string } | null;
  /**
   * Whether the survey is an exit questionnaire (`trigger: ON_OPT_OUT`).
   *
   * It changes the rule from a choice into a statement: the questionnaire belongs to one feature,
   * is asked at the instant somebody turns it off, and has no delay — the BFF ignores the delay,
   * the anchor and the required state for this trigger entirely. Drawing those controls anyway
   * would offer settings that do nothing and write a rule nothing reads.
   */
  isExitQuestionnaire?: boolean;
  error?: string;
  onToggle: (value: boolean) => void;
  onFeatureChange: (id: string) => void;
  onUseCaseChange: (id: string) => void;
  onStateChange: (state: OptInState) => void;
  onDelayAmountChange: (value: string) => void;
  onDelayUnitChange: (unit: DelayUnit) => void;
  onAnchorChange: (anchor: DelayAnchor) => void;
  onEligibleFromChange: (value: string) => void;
  onMaxDaysChange: (value: string) => void;
}

export const SurveyEligibilityBlock = ({
  form,
  featureOptions,
  useCaseOptions,
  conflict,
  isExitQuestionnaire = false,
  error,
  onToggle,
  onFeatureChange,
  onUseCaseChange,
  onStateChange,
  onDelayAmountChange,
  onDelayUnitChange,
  onAnchorChange,
  onEligibleFromChange,
  onMaxDaysChange,
}: SurveyEligibilityBlockProps): JSX.Element => {
  /** The narrowing a saved survey carries, if any — only the API can set one for this trigger. */
  const selectedUseCaseName = form.useCaseId
    ? (useCaseOptions.find(option => option.id === form.useCaseId)?.name ?? form.useCaseId)
    : null;

  /*
    Names the audience instead of asserting it. "Todos os que recebem a funcionalidade" is true but
    empty on its own — whoever is deciding wants to know who that is, and the feature's own use
    cases are already in `useCaseOptions`.
  */
  const featureUseCaseSummary =
    useCaseOptions.length === 0
      ? 'Mesmo público da funcionalidade: todos os use cases da BU.'
      : `Mesmo público da funcionalidade: ${useCaseOptions.map(option => option.name).join(', ')}.`;

  const refinementCount =
    (form.useCaseId.trim() === '' ? 0 : 1) +
    (form.maxDaysSinceAnchor.trim() === '' ? 0 : 1) +
    (form.eligibleOptInFrom.trim() === '' ? 0 : 1);
  const [isRefineOpen, setIsRefineOpen] = useState(refinementCount > 0 || Boolean(conflict));

  const feature = featureOptions.find(option => option.id === form.requiresOptInFeatureId);
  const useCaseName = useCaseOptions.find(option => option.id === form.useCaseId)?.name ?? null;

  return (
    <div className={panelStyle}>
      <h2 className={titleStyle}>Elegibilidade</h2>

      {/*
        With an exit questionnaire the condition is not a choice: the questionnaire IS the exit of
        one feature, and the checkbox would be a control with one legal position.
      */}
      {isExitQuestionnaire ? (
        <div id='survey-eligibility-exit-note' className={toggleTextStyle}>
          <span className={toggleLabelStyle}>Vinculada a uma funcionalidade, obrigatoriamente</span>
          <span className={toggleHintStyle}>
            Este é o questionário de saída de uma funcionalidade opt-in: ele aparece{' '}
            <strong>só dentro do modal de desativar</strong>, nunca sozinho, e o único caminho até
            ele é a funcionalidade abaixo. Sem ela, a pesquisa fica aprovada, ativa e perguntada a
            ninguém — por isso o BFF recusa salvar assim.
          </span>
        </div>
      ) : null}

      {isExitQuestionnaire ? null : (
        <label
          className={toggleRowStyle}
          htmlFor='survey-eligibility-condition'
          aria-label='Condicionar a uma funcionalidade opt-in'
        >
          <input
            id='survey-eligibility-condition'
            type='checkbox'
            checked={form.conditionOnOptIn}
            onChange={event => {
              onToggle(event.target.checked);
            }}
          />
          <span className={toggleTextStyle}>
            <span className={toggleLabelStyle}>Condicionar a uma funcionalidade opt-in</span>
            <span className={toggleHintStyle}>
              Desmarcado, a pesquisa alcança todo usuário da plataforma neste tenant — e a tela de
              resultados fica <strong>sem taxa de resposta</strong>, porque o cadastro de quem é
              usuário da plataforma mora no org backend, não neste serviço.
            </span>
          </span>
        </label>
      )}

      {isExitQuestionnaire ? (
        <>
          <div id='survey-eligibility-rule' className={sentenceStyle}>
            Responde este questionário quem estiver desativando{' '}
            <select
              id='survey-eligibility-feature'
              className={`${inlineSelectStyle} ${feature ? '' : missingSelectStyle}`}
              value={form.requiresOptInFeatureId}
              aria-label='Funcionalidade opt-in'
              onChange={event => {
                onFeatureChange(event.target.value);
              }}
            >
              <option value=''>
                {featureOptions.length === 0
                  ? '(nenhuma funcionalidade nesta BU)'
                  : '(escolha a funcionalidade)'}
              </option>
              {featureOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {option.key}
                </option>
              ))}
            </select>
            , no instante em que desativa.
          </div>

          {error ? <span className={errorStyle}>{error}</span> : null}

          {/*
            The use case still applies — the BFF checks it for this trigger too — but the delay, the
            anchor and the "só quem entrou a partir de" are not read at all here, so they are not
            offered. There is no waiting: the question is asked at the moment of leaving.
          */}
          <div id='survey-eligibility-refine' className={refineBodyStyle}>
            <div className={refineFieldStyle}>
              <span className={labelStyle}>Use case</span>
              {/*
                Fixed for an exit questionnaire, and fixed on "todos" rather than on the feature's
                use cases — which is the same audience, written the only way this field can write
                it.

                The field NARROWS inside the feature's audience, and a feature can be offered to
                several use cases while a survey carries one. Copying one of them here would ask
                half the leavers and stay silent about the other half. Empty is not "unset": it is
                "everyone who receives the feature", which is exactly the opt-in's own audience.

                An existing value is shown rather than cleared: only the API can set one, and
                widening somebody's audience on save, without saying so, is worse than showing a
                narrowing this screen cannot undo.
              */}
              {isExitQuestionnaire ? (
                <>
                  <span id='survey-eligibility-use-case' className={lockedUseCaseStyle}>
                    {selectedUseCaseName ?? 'Todos os que recebem a funcionalidade'}
                  </span>
                  <span className={refineHintStyle}>
                    {selectedUseCaseName
                      ? 'Estreitado por API. Esta tela não altera o use case de um questionário de saída.'
                      : featureUseCaseSummary}
                  </span>
                </>
              ) : (
                <select
                  id='survey-eligibility-use-case'
                  className={refineSelectStyle}
                  value={form.useCaseId}
                  onChange={event => {
                    onUseCaseChange(event.target.value);
                  }}
                >
                  <option value=''>Todos os que recebem a funcionalidade</option>
                  {useCaseOptions.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              )}
              {isExitQuestionnaire ? null : conflict ? (
                <span id='survey-eligibility-conflict' className={conflictStyle}>
                  A funcionalidade {conflict.featureName} não é oferecida ao use case{' '}
                  {conflict.useCaseName}. Ninguém satisfaz as duas condições, então este
                  questionário não é perguntado a ninguém — escolha outro use case ou volte para
                  todos.
                </span>
              ) : (
                <span className={refineHintStyle}>
                  O público já vem limitado a quem pôde ligar a funcionalidade.
                </span>
              )}
            </div>
          </div>
        </>
      ) : null}

      {isExitQuestionnaire || form.conditionOnOptIn ? null : (
        <div id='survey-eligibility-rule' className={sentenceStyle}>
          Recebe a pesquisa{' '}
          <select
            id='survey-eligibility-use-case'
            className={inlineSelectStyle}
            value={form.useCaseId}
            aria-label='Público da pesquisa'
            onChange={event => {
              onUseCaseChange(event.target.value);
            }}
          >
            <option value=''>todo usuário da BU</option>
            {useCaseOptions.map(option => (
              <option key={option.id} value={option.id}>
                quem for do use case {option.name}
              </option>
            ))}
          </select>
          .
        </div>
      )}

      {!isExitQuestionnaire && form.conditionOnOptIn ? (
        <>
          <div id='survey-eligibility-rule' className={sentenceStyle}>
            Recebe a pesquisa quem estiver{' '}
            <select
              id='survey-eligibility-state'
              className={inlineSelectStyle}
              value={form.requiredOptInState}
              aria-label='Estado exigido da funcionalidade'
              onChange={event => {
                onStateChange(event.target.value as OptInState);
              }}
            >
              {(Object.keys(ELIGIBILITY_STATE_CLAUSES) as OptInState[]).map(state => (
                <option key={state} value={state}>
                  {ELIGIBILITY_STATE_CLAUSES[state]}
                </option>
              ))}
            </select>{' '}
            em{' '}
            <select
              id='survey-eligibility-feature'
              className={`${inlineSelectStyle} ${feature ? '' : missingSelectStyle}`}
              value={form.requiresOptInFeatureId}
              aria-label='Funcionalidade opt-in'
              onChange={event => {
                onFeatureChange(event.target.value);
              }}
            >
              <option value=''>
                {featureOptions.length === 0
                  ? '(nenhuma funcionalidade nesta BU)'
                  : '(escolha a funcionalidade)'}
              </option>
              {featureOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {option.key}
                </option>
              ))}
            </select>{' '}
            há pelo menos{' '}
            <span className={inlineNumberStyle}>
              <UTTextField
                id='survey-eligibility-delay-amount'
                type='number'
                value={form.delayAmount}
                aria-label='Tempo desde a âncora'
                onChange={event => {
                  onDelayAmountChange(event.target.value);
                }}
              />
            </span>{' '}
            {/* The unit sits beside the amount and is always sent with it: the BFF reads a missing
                unit as days without complaining, so "5" meant as minutes would quietly become five
                days. Days stay the default; minutes are how the rule gets watched working. */}
            <select
              id='survey-eligibility-delay-unit'
              className={inlineSelectStyle}
              value={form.delayUnit}
              aria-label='Unidade do tempo'
              onChange={event => {
                onDelayUnitChange(event.target.value as DelayUnit);
              }}
            >
              {(Object.keys(ELIGIBILITY_UNIT_CLAUSES) as DelayUnit[]).map(unit => (
                <option key={unit} value={unit}>
                  {ELIGIBILITY_UNIT_CLAUSES[unit]}
                </option>
              ))}
            </select>
            , contados{' '}
            <select
              id='survey-eligibility-anchor'
              className={inlineSelectStyle}
              value={form.delayAnchor}
              aria-label='Âncora da contagem'
              onChange={event => {
                onAnchorChange(event.target.value as DelayAnchor);
              }}
            >
              {(Object.keys(ELIGIBILITY_ANCHOR_CLAUSES) as DelayAnchor[]).map(anchor => (
                <option key={anchor} value={anchor}>
                  {ELIGIBILITY_ANCHOR_CLAUSES[anchor]}
                </option>
              ))}
            </select>
            .
          </div>

          {error ? <span className={errorStyle}>{error}</span> : null}

          <button
            type='button'
            id='survey-eligibility-refine-toggle'
            className={refineToggleStyle}
            aria-expanded={isRefineOpen}
            onClick={() => {
              setIsRefineOpen(current => !current);
            }}
          >
            <GLIcon
              variant={isRefineOpen ? 'Arrows.CaretUp' : 'Arrows.CaretDown'}
              size={12}
              fill='currentColor'
            />
            Refinar quem recebe
            {refinementCount > 0 && !isRefineOpen
              ? ` · ${refinementCount} ${refinementCount === 1 ? 'recorte ativo' : 'recortes ativos'}`
              : ''}
          </button>

          {isRefineOpen ? (
            <div id='survey-eligibility-refine' className={refineBodyStyle}>
              {/* Only this feature's use cases are offered, so the empty intersection of R10b has
                  nowhere to be written. */}
              <div className={refineFieldStyle}>
                <span className={labelStyle}>Estreitar por use case</span>
                <select
                  id='survey-eligibility-use-case'
                  className={refineSelectStyle}
                  value={form.useCaseId}
                  onChange={event => {
                    onUseCaseChange(event.target.value);
                  }}
                >
                  <option value=''>Todos os que recebem a funcionalidade</option>
                  {useCaseOptions.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
                {conflict ? (
                  <span id='survey-eligibility-conflict' className={conflictStyle}>
                    A funcionalidade {conflict.featureName} não é oferecida ao use case{' '}
                    {conflict.useCaseName}. Ninguém satisfaz as duas condições, então esta pesquisa
                    não alcança nenhuma pessoa — escolha outro use case ou volte para todos.
                  </span>
                ) : (
                  <span className={refineHintStyle}>
                    O público já vem limitado a quem pôde ligar a funcionalidade.
                  </span>
                )}
              </div>

              <div className={refineFieldStyle}>
                <span className={labelStyle}>Limite de dias</span>
                <span className={narrowFieldStyle}>
                  <UTTextField
                    id='survey-eligibility-max-days'
                    type='number'
                    placeholder='sem limite'
                    value={form.maxDaysSinceAnchor}
                    onChange={event => {
                      onMaxDaysChange(event.target.value);
                    }}
                  />
                </span>
                <span className={refineHintStyle}>Pula quem já passou desse tempo.</span>
              </div>

              <div className={refineFieldStyle}>
                <span className={labelStyle}>Só quem entrou a partir de</span>
                <input
                  id='survey-eligibility-from'
                  type='datetime-local'
                  className={dateInputStyle}
                  value={form.eligibleOptInFrom}
                  onChange={event => {
                    onEligibleFromChange(event.target.value);
                  }}
                />
                <span className={refineHintStyle}>
                  Corta a represa: sem isso, todo mundo que já usa a funcionalidade fica elegível no
                  mesmo dia em que a pesquisa é aprovada.
                </span>
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      {/* The rule read back in plain Portuguese, whichever shape it took: where someone catches
          the select they changed without meaning to. An exit questionnaire has no rule to echo —
          its sentence above already is the whole of it. */}
      {isExitQuestionnaire ? null : (
        <div id='survey-eligibility-echo' className={echoStyle}>
          <GLIcon variant='Communication.Info' size={15} fill='var(--color-text-muted)' />
          <span>
            {eligibilitySentence({
              conditionOnOptIn: form.conditionOnOptIn,
              featureName: feature?.name ?? null,
              state: form.requiredOptInState,
              delayAmount: form.delayAmount,
              delayUnit: form.delayUnit,
              anchor: form.delayAnchor,
              maxDaysSinceAnchor: form.maxDaysSinceAnchor,
              useCaseName,
            })}
          </span>
        </div>
      )}
    </div>
  );
};
